/**
 * In-browser stale-while-revalidate cache (task 4.6, public-website spec).
 *
 * Per design §5.2 and Requirement 10.2, this module wraps `httpClient`
 * with a stale-while-revalidate cache keyed by URL + auth headers
 * (`Authorization` + `x-api-key` per design §5.1 — the only two headers
 * the public REST API surface keys responses on). On every fetch:
 *
 * 1. Read the cached entry. If present, return it immediately and
 *    schedule a background fetch in a microtask.
 * 2. The background fetch parses the JSON response and runs a
 *    deep-equal check against the cached body. The cache is updated
 *    unconditionally; the `subscription.onRevalidate(next)` callback
 *    is invoked **only** when the body differs (so the React tree
 *    re-renders only on real change).
 *
 * Cache layering follows design §5.2: an in-memory `Map` (process-
 * lifetime) backed by `sessionStorage` (cross-tab persistence within
 * one session). `sessionStorage` is read on miss and populates the
 * in-memory layer; writes go to both. Failures from `sessionStorage`
 * (quota, SSR, locked storage) degrade gracefully to memory-only.
 *
 * Bypass rules per Requirement 10.3 + 10.4:
 * - URLs matching `/Profile*V1` (the auth-gated routes —
 *   `GetProfileV1`, `UpsertProfileV1`, `ConfirmEmailV1`) bypass SWR
 *   entirely. Profile data is held in the Redux auth slice; never
 *   cached here.
 * - Requests carrying a `Cache-Control: no-store` header bypass SWR
 *   entirely (no read, no write).
 * - Responses carrying a `Cache-Control: no-store` header are
 *   returned to the caller but are not written to the cache.
 *
 * Function-length budget: every function below stays under 20 lines
 * per Requirement 3.7. Deep-equal is decomposed into `deepEqualArrays`
 * + `deepEqualObjects`; the main `swrFetch` is a six-line composition.
 */

import { httpClient, mergeHeaders, type HttpClientRequestInit } from './http-client';

/** One row of the SWR cache. */
export interface SwrCacheEntry<T = unknown> {
  readonly key: string;
  readonly value: T;
  readonly cachedAt: number;
}

/**
 * Per-request init for `swrFetch`. Identical to `HttpClientRequestInit`
 * — exported as its own name to keep the SWR layer's surface explicit.
 */
export type SwrFetchOptions = HttpClientRequestInit;

/** Subscription invoked when background revalidation finds a different body. */
export interface SwrSubscription<T> {
  readonly onRevalidate: (next: T) => void;
}

const SESSION_PREFIX = 'dvgup-swr:';
const PROFILE_BYPASS_PATTERN = /\/Profile\w*V1\b/;
const memoryCache: Map<string, SwrCacheEntry> = new Map();

/**
 * Build the SWR cache key for a request. The key combines the URL,
 * the two auth-bearing headers per design §5.1, and a hash of the
 * request body. The body MUST be included because the public REST
 * surface uses POST with a JSON body to discriminate between
 * semantically different requests on the same URL — for example, the
 * SPA's `content-api-client.ts` issues three parallel
 * `POST /GetApprovedContentV1` calls (one per `type`) to the same
 * URL with the same auth headers, and previously the cache served
 * the first response back to all three callers.
 */
function buildCacheKey(input: string, init?: SwrFetchOptions): string {
  const headers = mergeHeaders(init);
  const auth = headers.get('Authorization') ?? '';
  const apiKey = headers.get('x-api-key') ?? '';
  const method = (init?.method ?? 'GET').toUpperCase();
  const body = typeof init?.body === 'string' ? init.body : '';
  return `${method} ${input}|auth:${auth}|key:${apiKey}|body:${body}`;
}

/**
 * Decide whether a request should bypass SWR per Requirement 10.3 / 10.4.
 */
function shouldBypass(input: string, init?: SwrFetchOptions): boolean {
  if (PROFILE_BYPASS_PATTERN.test(input)) return true;
  const cacheControl = mergeHeaders(init).get('Cache-Control') ?? '';
  return /no-store/i.test(cacheControl);
}

/** Read a JSON-encoded entry from `sessionStorage`. */
function readSessionStorage<T>(key: string): SwrCacheEntry<T> | undefined {
  try {
    const raw = globalThis.sessionStorage?.getItem(SESSION_PREFIX + key);
    return raw ? (JSON.parse(raw) as SwrCacheEntry<T>) : undefined;
  } catch {
    return undefined;
  }
}

/** Write a JSON-encoded entry to `sessionStorage`. */
function writeSessionStorage<T>(key: string, entry: SwrCacheEntry<T>): void {
  try {
    globalThis.sessionStorage?.setItem(SESSION_PREFIX + key, JSON.stringify(entry));
  } catch {
    // sessionStorage unavailable (SSR / quota / locked). Memory only.
  }
}

/** Read the cache for `key`, falling back from memory to sessionStorage. */
function readCache<T>(key: string): SwrCacheEntry<T> | undefined {
  const inMemory = memoryCache.get(key) as SwrCacheEntry<T> | undefined;
  if (inMemory) return inMemory;
  const fromSession = readSessionStorage<T>(key);
  if (fromSession) memoryCache.set(key, fromSession);
  return fromSession;
}

/** Write the cache for `key` to both memory and sessionStorage. */
function writeCache<T>(key: string, value: T): SwrCacheEntry<T> {
  const entry: SwrCacheEntry<T> = { key, value, cachedAt: Date.now() };
  memoryCache.set(key, entry);
  writeSessionStorage(key, entry);
  return entry;
}

/** Element-wise array equality, recursing into `deepEqual`. */
function deepEqualArrays(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, index) => deepEqual(value, b[index]));
}

/** Key-set + value-wise object equality, recursing into `deepEqual`. */
function deepEqualObjects(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => key in b && deepEqual(a[key], b[key]));
}

/**
 * Recursive structural equality for parsed JSON values. Sufficient for
 * the JSON subset (no Dates, Maps, Sets, or class instances).
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a === null || b === null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) && Array.isArray(b)) return deepEqualArrays(a, b);
  if (Array.isArray(a) || Array.isArray(b)) return false;
  return deepEqualObjects(a as Record<string, unknown>, b as Record<string, unknown>);
}

/** Issue the request via `httpClient` and parse the JSON body. */
async function fetchAndParse<T>(
  input: string,
  init?: SwrFetchOptions,
): Promise<{ value: T; noStore: boolean }> {
  const response = await httpClient(input, init);
  const noStore = /no-store/i.test(response.headers.get('Cache-Control') ?? '');
  // Throw on non-2xx so the caller's catch fires (Redux thunk
  // dispatches `failed` instead of treating an error envelope as a
  // successful payload). Without this, a 400 / 500 response body
  // (`{errorCode, message, correlationId}`) flows through to the
  // slice as a "success" with no `items` array and the downstream
  // selector crashes on `data.items.length`.
  if (!response.ok) {
    let detail = '';
    try {
      const errBody = (await response.clone().json()) as {
        errorCode?: string;
        message?: string;
      };
      detail = errBody.message ? ` (${errBody.errorCode ?? 'error'}: ${errBody.message})` : '';
    } catch {
      // Body wasn't JSON — leave detail blank.
    }
    throw new Error(`HTTP ${response.status} ${response.statusText}${detail}`);
  }
  const value = (await response.json()) as T;
  return { value, noStore };
}

/**
 * Background revalidation for a cache hit. Updates the cache and
 * fires `subscription.onRevalidate` only when the parsed body differs
 * from the cached body.
 */
async function revalidate<T>(
  key: string,
  input: string,
  init: SwrFetchOptions | undefined,
  cached: T,
  subscription?: SwrSubscription<T>,
): Promise<void> {
  try {
    const { value, noStore } = await fetchAndParse<T>(input, init);
    if (!noStore) writeCache(key, value);
    if (!deepEqual(value, cached)) subscription?.onRevalidate(value);
  } catch {
    // Background revalidation failures are silent per design §5.2.
  }
}

/**
 * Stale-while-revalidate fetch wrapping `httpClient` per design §5.2.
 *
 * @param input The request URL.
 * @param init Optional per-request init (auth headers, body, etc.).
 * @param subscription Optional callback fired when background
 *   revalidation finds a different body than the cached body.
 * @returns The parsed JSON body. On a cache hit, returns the cached
 *   body immediately; on a miss, awaits the network response.
 */
export async function swrFetch<T>(
  input: string,
  init?: SwrFetchOptions,
  subscription?: SwrSubscription<T>,
): Promise<T> {
  if (shouldBypass(input, init)) {
    const { value } = await fetchAndParse<T>(input, init);
    return value;
  }
  const key = buildCacheKey(input, init);
  const cached = readCache<T>(key);
  if (cached) {
    void Promise.resolve().then(() => revalidate(key, input, init, cached.value, subscription));
    return cached.value;
  }
  const { value, noStore } = await fetchAndParse<T>(input, init);
  if (!noStore) writeCache(key, value);
  return value;
}

/**
 * Test-only helper: clear the in-memory cache and the sessionStorage
 * entries this module owns. Not part of the production surface; used
 * by `tst/lib/api/swr-cache.test.ts` to isolate test cases.
 *
 * @internal
 */
export function _clearSwrCache(): void {
  memoryCache.clear();
  try {
    const keys: string[] = [];
    for (let i = 0; i < (globalThis.sessionStorage?.length ?? 0); i += 1) {
      const key = globalThis.sessionStorage.key(i);
      if (key && key.startsWith(SESSION_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => globalThis.sessionStorage.removeItem(key));
  } catch {
    // sessionStorage unavailable; nothing to clear there.
  }
}
